import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Car, Clock, Accessibility, Zap, Clock3, Plus, Layers, Settings, AlertCircle } from 'lucide-react'
import { parkingSpotApi, parkingConfigurationApi } from '../services/api'
import { stripHtml } from '../utils/htmlHelpers'
import {
  SearchBar,
  Pagination,
  LoadingSpinner,
  EmptyState,
  Button,
  Card,
  CardBody,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Badge
} from '../components/common'

const ParkingSpots = ({ buildingId }) => {
  const [parkingSpots, setParkingSpots] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [parkingConfig, setParkingConfig] = useState(null)

  const pageSize = 10

  const loadParkingSpots = async () => {
    if (!buildingId) return
    try {
      setLoading(true)
      const response = await parkingSpotApi.getPaged(buildingId, {
        pageNumber: currentPage,
        pageSize,
        ...(searchTerm && { searchString: searchTerm }),
      })
      const data = response.data?.data || response.data
      const items = data?.items || data || []
      setParkingSpots(Array.isArray(items) ? items : [])
      setTotalPages(data?.totalPages || 1)
      setTotalCount(data?.totalCount || (Array.isArray(items) ? items.length : 0))
    } catch (error) {
      console.error('Error loading parking spots:', error)
      setParkingSpots([])
      setTotalPages(1)
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (buildingId) {
      loadParkingSpots()
      loadParkingConfig()
    } else {
      setParkingConfig(null)
      setParkingSpots([])
      setLoading(false)
    }
  }, [buildingId, currentPage, searchTerm])

  const loadParkingConfig = async () => {
    try {
      const response = await parkingConfigurationApi.get(buildingId)
      const data = response.data?.data || response.data
      setParkingConfig(data && data.parkingConfigurationId ? data : null)
    } catch (err) {
      setParkingConfig(null)
    }
  }

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput)
        setCurrentPage(1)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchInput, searchTerm])

  const handleSearch = (term) => {
    setSearchInput(term)
  }

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setSearchTerm(searchInput)
      setCurrentPage(1)
    }
  }

  const formatTime = (time) => {
    if (!time) return 'N/A'
    return time.substring(0, 5)
  }

  if (!buildingId) return null

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-end">
        <div className="flex-1">
          <SearchBar
            value={searchInput}
            onChange={handleSearch}
            onKeyDown={handleSearchSubmit}
            placeholder="Search parking spots... (Press Enter to search)"
          />
        </div>
        <div className="flex gap-2">
          <Button
            as={Link}
            to={`/parking/${buildingId}/batch-create`}
            variant="secondary"
            size="small"
            icon={<Layers className="w-4 h-4" />}
          >
            Batch Create
          </Button>
          <Button
            as={Link}
            to={`/parking/${buildingId}/create`}
            variant="primary"
            size="small"
            icon={<Plus className="w-4 h-4" />}
          >
            Create Spot
          </Button>
        </div>
      </div>

      {/* Config context banner */}
      {!loading && (
        parkingConfig ? (
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <Settings className="w-4 h-4" />
            <span>Parking Type: <strong>{parkingConfig.parkingTypeName}</strong></span>
            {parkingConfig.totalCapacity && (
              <span className="ml-2">| Capacity: <strong>{parkingConfig.totalCapacity}</strong></span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            <AlertCircle className="w-4 h-4" />
            <span>No parking configuration set for this building. Switch to the Configuration tab to set one up.</span>
          </div>
        )
      )}

      {loading ? (
        <LoadingSpinner />
      ) : parkingSpots.length === 0 ? (
        <EmptyState
          icon={Car}
          title="No parking spots found"
          description="This building has no parking spots configured."
        />
      ) : (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>Parking Spot</TableCell>
                  <TableCell header>Spot Number</TableCell>
                  <TableCell header>Floor</TableCell>
                  <TableCell header>Available Hours</TableCell>
                  <TableCell header>Features</TableCell>
                  <TableCell header>Reservations</TableCell>
                  <TableCell header>Roles</TableCell>
                  <TableCell header>
                    <span className="sr-only">Actions</span>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parkingSpots.map((spot) => (
                  <TableRow key={spot.parkingSpotId} clickable>
                    <TableCell>
                      <div className="flex items-center">
                        <div className="p-2 bg-primary-100 rounded-lg mr-3">
                          <Car className="w-4 h-4 text-primary-600" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {spot.name || 'Unnamed Spot'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-900">
                        {spot.spotNumber || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {spot.floor || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 mr-1" />
                        {spot.isAvailable24_7
                          ? '24/7'
                          : `${formatTime(spot.startTime)} - ${formatTime(spot.endTime)}`
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {spot.isHandicapAccessible && (
                          <Badge variant="info" size="small" title="Handicap Accessible">
                            <Accessibility className="w-3 h-3 mr-1" /> Accessible
                          </Badge>
                        )}
                        {spot.isElectricCharging && (
                          <Badge variant="success" size="small" title="EV Charging">
                            <Zap className="w-3 h-3 mr-1" /> EV
                          </Badge>
                        )}
                        {spot.isAvailable24_7 && (
                          <Badge variant="secondary" size="small" title="Available 24/7">
                            <Clock3 className="w-3 h-3 mr-1" /> 24/7
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="info" size="small">
                        {spot.totalReservations || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {stripHtml(spot.rolesDescription) || 'All roles'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        as={Link}
                        to={`/parking/${buildingId}/spots/${spot.parkingSpotId}`}
                        variant="ghost"
                        size="small"
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}

          <div className="text-sm text-gray-500">{totalCount} parking spots found</div>
        </>
      )}
    </div>
  )
}

export default ParkingSpots
